import * as THREE from 'three/webgpu';
import type {SketchGroup} from '../../mesh/sketch/SketchGroup';
import type {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import type {AbstractSketchEdgeObject} from '../../mesh/sketch/AbstractSketchEdgeObject';
import type {SketchSlotObject} from '../../mesh/sketch/SketchSlotObject';
import type {SketchCollectionObject} from '../../mesh/sketch/SketchCollectionObject';
import {SketchCircularPatternObject} from '../circular-pattern/SketchCircularPatternObject';
import {SketchPointBoxSelectionRole, type ISketchObject} from '../../mesh/sketch/ISketchObject';
import type {SketchRelationDefinition} from '../solver/constraint/relationDefinition/SketchRelationDefinition';
import {
    SketchRelationKind,
    readSketchRelationNumericValue,
} from '../solver/constraint/relation-compatibility';
import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchCollectionObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchPointObject,
    isSketchSlotObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../../mesh/guards';
import {resolveSupportedPersistentRelationKindSet} from './supportedSketchRelationKinds';
import {remapSketchPersistentPlainDataIds} from './remapSketchPersistentPlainDataIds';
import {
    SKETCH_PERSISTENT_DOCUMENT_SCHEMA_VERSION_V1,
    type SketchPersistentDocument,
    type SketchPersistentEntityRecord,
    type SketchPersistentPlainData,
    type SketchPersistentPointRecord,
    type SketchPersistentReferencePlaneRecord,
    type SketchPersistentRelationRecord,
    type SketchPersistentVector3,
} from './SketchPersistenceTypes';
import type {SketchPersistentCircularPatternRecord} from '../../../types/sketch/circular-pattern/SketchCircularPatternTypes';
import type {SketchPersistentMirrorAxisReferenceRecord} from '../../../types/sketch/mirror/SketchMirrorAxisReferenceTypes';

/**
 * 将 THREE.Vector3 转换为持久化三元组。
 * @param vector 源向量。
 * @returns 持久化三元组。
 */
function toVector3Tuple(vector: THREE.Vector3): SketchPersistentVector3 {
    return [vector.x, vector.y, vector.z];
}

/**
 * 草图组持久化序列化器。
 *
 * 职责：
 * 1. 把运行时 SketchGroup 全量实体（点/直线/圆/圆弧/椭圆/抛物线/样条/槽口/集合）与几何关系
 *    转换为纯数据 schema 文档；
 * 2. 容器语义：槽口/集合的成员边以普通实体记录输出，容器记录按持久 ID 引用成员；
 * 3. 输出确定性文档：数组按持久 ID 字典序排序，payload 不携带时间戳/随机序号；
 * 4. 完整性优先：未覆盖的实体/关系类型、引用不可序列化目标的关系直接抛错，禁止静默丢弃。
 *
 * 边界：
 * 1. 只序列化定义层事实与随对象保存的定义元数据（geometryData、构造/辅助标志、样条元数据）；
 * 2. 绘制预览对象、会话级参考点不进入文档；辅助对象（如矩形构造线交点）随定义保留。
 */
export class SketchGroupPersistenceSerializer {
    /**
     * 序列化草图组。
     * @param group 目标草图组。
     * @returns 持久化文档。
     */
    public serialize(group: SketchGroup): SketchPersistentDocument {
        const referencePlane = this.serializeReferencePlane(group);
        const pointRecordMap = new Map<string, SketchPersistentPointRecord>();
        const serializableObjectIdMap = new Map<ISketchObject, string>();
        const entityRecords: SketchPersistentEntityRecord[] = [];
        const mirrorAxisReferenceRecords: SketchPersistentMirrorAxisReferenceRecord[] = [];

        const edges: AbstractSketchEdgeObject[] = [];
        const slots: SketchSlotObject[] = [];
        const collections: SketchCollectionObject[] = [];
        group.getSketchObjects().forEach((sketchObject) => {
            if (isSketchPointObject(sketchObject)) {
                return;
            }
            // 绘制预览对象是运行态临时显示，不进入持久化文档。
            if (
                Reflect.get(sketchObject, 'isDrawingPreviewLine') === true
                || Reflect.get(sketchObject, 'isDrawingPreviewArc') === true
            ) {
                return;
            }
            if (isSketchSlotObject(sketchObject)) {
                slots.push(sketchObject);
                return;
            }
            if (isSketchCollectionObject(sketchObject)) {
                collections.push(sketchObject);
                return;
            }
            edges.push(sketchObject as AbstractSketchEdgeObject);
        });
        group.getSketchReferenceTargets().forEach((referenceTarget) => {
            serializableObjectIdMap.set(referenceTarget, referenceTarget.persistentId);
            mirrorAxisReferenceRecords.push(referenceTarget.toPersistentRecord());
        });

        /**
         * 仅由多条圆系曲线共享、没有被直线或容器引用的圆心，也必须进入既有点池。
         * 按对象身份计数可避免把两个误用同一持久 ID 的不同对象伪装成共享拓扑。
         */
        const curveCenterReferenceCountMap = new Map<SketchPointObject, number>();
        edges.forEach((edge) => {
            const centerPoint = isSketchCircleObject(edge)
                ? edge.getCenterControlPoint()
                : isSketchArcObject(edge)
                    ? edge.getCenterPoint()
                    : null;
            if (centerPoint) {
                curveCenterReferenceCountMap.set(
                    centerPoint,
                    (curveCenterReferenceCountMap.get(centerPoint) ?? 0) + 1,
                );
            }
        });
        curveCenterReferenceCountMap.forEach((referenceCount, centerPoint) => {
            if (referenceCount > 1) {
                this.registerPoolPoint(centerPoint, pointRecordMap, serializableObjectIdMap);
            }
        });

        edges.forEach((edge) => {
            entityRecords.push(this.serializeEdge(edge, pointRecordMap, serializableObjectIdMap));
        });
        slots.forEach((slot) => {
            entityRecords.push(this.serializeSlot(slot, pointRecordMap, serializableObjectIdMap));
        });
        collections.forEach((collection) => {
            entityRecords.push(this.serializeCollection(collection, pointRecordMap, serializableObjectIdMap));
        });
        this.collectFreePoints(group, pointRecordMap, serializableObjectIdMap);

        /**
         * 点池引用与私有圆心声明最终都会进入既有对象注册表；在这里统一反向校验对象身份，
         * 防止两个不同点仅因持久 ID 和坐标相同而在重载后被静默合并为共享拓扑。
         */
        const pointObjectByPersistentId = new Map<string, SketchPointObject>();
        serializableObjectIdMap.forEach((persistentId, sketchObject) => {
            if (!isSketchPointObject(sketchObject)) {
                return;
            }
            const registeredPoint = pointObjectByPersistentId.get(persistentId);
            if (registeredPoint && registeredPoint !== sketchObject) {
                throw new Error(`草图持久化序列化失败：不同草图点复用了同一持久 ID（${persistentId}）。`);
            }
            pointObjectByPersistentId.set(persistentId, sketchObject);
        });

        const relations = this.serializeRelations(serializableObjectIdMap);
        const circularPatternObjects: SketchCircularPatternObject[] = [];
        group.traverse((object) => {
            if (object instanceof SketchCircularPatternObject) {
                circularPatternObjects.push(object);
            }
        });
        const circularPatterns = circularPatternObjects
            .map((pattern) => this.serializeCircularPattern(pattern))
            .sort((left, right) => left.persistentId.localeCompare(right.persistentId));

        //------ geometryData 引用归一化 开始------
        /**
         * geometryData 内部以运行时 uuid 引用其它草图对象（矩形构造线联动等）。
         * 全部对象登记完成后统一做 uuid → 持久 ID 归一化，保证载荷跨会话可解析。
         */
        const uuidToPersistentId = new Map<string, string>();
        serializableObjectIdMap.forEach((persistentId, sketchObject) => {
            uuidToPersistentId.set(sketchObject.uuid, persistentId);
        });
        const normalizedEntityRecords = entityRecords.map((record) => (
            record.kind === 'slot' || record.kind === 'collection'
                ? {...record, geometryData: remapSketchPersistentPlainDataIds(record.geometryData, uuidToPersistentId)}
                : record
        ));
        //------ geometryData 引用归一化 结束------

        return {
            schemaVersion: SKETCH_PERSISTENT_DOCUMENT_SCHEMA_VERSION_V1,
            sketchId: group.persistentId,
            name: group.name,
            referencePlane,
            points: [...pointRecordMap.values()].sort((left, right) => left.pointId.localeCompare(right.pointId)),
            entities: normalizedEntityRecords.sort((left, right) => left.entityId.localeCompare(right.entityId)),
            relations: [...relations].sort((left, right) => left.constraintId.localeCompare(right.constraintId)),
            ...(circularPatterns.length > 0 ? {circularPatterns} : {}),
            ...(mirrorAxisReferenceRecords.length > 0
                ? {
                    mirrorAxisReferences: mirrorAxisReferenceRecords.sort(
                        (left, right) => left.targetId.localeCompare(right.targetId),
                    ),
                }
                : {}),
        };
    }

    /**
     * 序列化圆周草图阵列定义。
     *
     * @param pattern 圆周草图阵列定义对象。
     * @returns 仅包含持久 ID、核心单位参数和稳定实例映射的记录。
     */
    private serializeCircularPattern(pattern: SketchCircularPatternObject): SketchPersistentCircularPatternRecord {
        const snapshot = pattern.getDefinitionSnapshot();
        return {
            kind: 'circular-pattern',
            persistentId: snapshot.persistentId,
            sourceEntityPersistentIds: [...snapshot.sourceEntityPersistentIds],
            center2d: [snapshot.parameters.center2d[0], snapshot.parameters.center2d[1]],
            radius: snapshot.parameters.radius,
            startAngleRad: THREE.MathUtils.degToRad(snapshot.parameters.startAngleDeg),
            totalAngleRad: THREE.MathUtils.degToRad(snapshot.parameters.totalAngleDeg),
            equalSpacing: snapshot.parameters.equalSpacing,
            spacingAngleRad: THREE.MathUtils.degToRad(snapshot.parameters.spacingAngleDeg),
            reverseDirection: snapshot.parameters.reverseDirection,
            instanceCount: snapshot.parameters.instanceCount,
            rotateInstances: snapshot.parameters.rotateInstances,
            showRadiusDimension: snapshot.parameters.showRadiusDimension,
            showSpacingDimension: snapshot.parameters.showSpacingDimension,
            showInstanceCount: snapshot.parameters.showInstanceCount,
            skippedInstanceIndexes: [...snapshot.parameters.skippedInstanceIndexes],
            instances: snapshot.instances.map((instance) => ({
                instanceIndex: instance.instanceIndex,
                entityPersistentIds: [...instance.entityPersistentIds],
            })),
        };
    }

    /**
     * 序列化参考平面。
     * @param group 目标草图组。
     * @returns 参考平面记录。
     */
    private serializeReferencePlane(group: SketchGroup): SketchPersistentReferencePlaneRecord {
        const plane = group.referencePlane;
        return {
            referencePlaneId: plane.id,
            displayName: plane.displayName,
            normal: toVector3Tuple(plane.normal),
            constant: plane.constant,
            xAxis: toVector3Tuple(plane.xAxis),
            yAxis: toVector3Tuple(plane.yAxis),
            hostFace: plane.hostFace
                ? {objectId: plane.hostFace.objectId, faceIndex: plane.hostFace.faceIndex}
                : null,
        };
    }

    /**
     * 序列化单条边实体（含椭圆/抛物线/样条），并将其引用点登记进点池。
     * @param edge 边对象。
     * @param pointRecordMap 点池记录映射。
     * @param serializableObjectIdMap 可序列化对象到持久 ID 的映射。
     * @returns 实体记录。
     */
    private serializeEdge(
        edge: AbstractSketchEdgeObject,
        pointRecordMap: Map<string, SketchPersistentPointRecord>,
        serializableObjectIdMap: Map<ISketchObject, string>,
    ): SketchPersistentEntityRecord {
        serializableObjectIdMap.set(edge, edge.persistentId);

        if (isSketchLineObject(edge)) {
            this.registerPoolPoint(edge.startPoint, pointRecordMap, serializableObjectIdMap);
            this.registerPoolPoint(edge.endPoint, pointRecordMap, serializableObjectIdMap);
            return {
                entityId: edge.persistentId,
                kind: 'line',
                startPointId: edge.startPoint.persistentId,
                endPointId: edge.endPoint.persistentId,
                isConstruction: edge.isConstruction,
                isInfiniteLength: edge.isInfiniteLength === true,
                ...(edge.isCenterline === true ? {isCenterline: true} : {}),
                ...(edge.isAuxiliaryObject === true ? {isAuxiliaryObject: true} : {}),
            };
        }

        if (isSketchCircleObject(edge)) {
            const centerPoint = edge.getCenterControlPoint();
            serializableObjectIdMap.set(centerPoint, centerPoint.persistentId);
            return {
                entityId: edge.persistentId,
                kind: 'circle',
                centerWorld: toVector3Tuple(edge.getCenterWorld()),
                radius: edge.radius,
                centerPointId: centerPoint.persistentId,
                isConstruction: edge.isConstruction,
            };
        }

        if (isSketchArcObject(edge)) {
            const definition = edge.getArcDefinition();
            const centerPoint = edge.getCenterPoint();
            serializableObjectIdMap.set(centerPoint, centerPoint.persistentId);
            this.registerPoolPoint(edge.getStartPoint(), pointRecordMap, serializableObjectIdMap);
            this.registerPoolPoint(edge.getEndPoint(), pointRecordMap, serializableObjectIdMap);
            return {
                entityId: edge.persistentId,
                kind: 'arc',
                centerWorld: toVector3Tuple(definition.centerWorld),
                radius: definition.radius,
                startAngle: definition.startAngle,
                endAngle: definition.endAngle,
                clockwise: definition.clockwise,
                segments: definition.segments,
                startPointId: edge.getStartPoint().persistentId,
                endPointId: edge.getEndPoint().persistentId,
                centerPointId: centerPoint.persistentId,
                isConstruction: edge.isConstruction,
            };
        }

        if (isSketchEllipseObject(edge)) {
            const definition = edge.getEllipseDefinition();
            this.registerPoolPoint(edge.getStartPoint(), pointRecordMap, serializableObjectIdMap);
            this.registerPoolPoint(edge.getEndPoint(), pointRecordMap, serializableObjectIdMap);
            this.registerPoolPoint(edge.getCenterPoint(), pointRecordMap, serializableObjectIdMap);
            const cardinalControlPoints = [...edge.getCardinalControlPointsSnapshot().entries()]
                .map(([role, point]) => {
                    this.registerPoolPoint(point, pointRecordMap, serializableObjectIdMap);
                    return {role: role as string, pointId: point.persistentId};
                })
                .sort((left, right) => left.role.localeCompare(right.role));
            return {
                entityId: edge.persistentId,
                kind: 'ellipse',
                centerWorld: toVector3Tuple(definition.centerWorld),
                radiusX: definition.radiusX,
                radiusY: definition.radiusY,
                rotation: definition.rotation,
                startAngle: definition.startAngle,
                endAngle: definition.endAngle,
                clockwise: definition.clockwise,
                segments: definition.segments,
                startPointId: edge.getStartPoint().persistentId,
                endPointId: edge.getEndPoint().persistentId,
                centerPointId: edge.getCenterPoint().persistentId,
                cardinalControlPoints,
                isConstruction: edge.isConstruction,
                hasContour: edge.contour !== null,
            };
        }

        if (isSketchParabolaObject(edge)) {
            const definition = edge.getParabolaDefinition();
            this.registerPoolPoint(edge.getFocalPoint(), pointRecordMap, serializableObjectIdMap);
            this.registerPoolPoint(edge.getApexPoint(), pointRecordMap, serializableObjectIdMap);
            this.registerPoolPoint(edge.getStartPoint(), pointRecordMap, serializableObjectIdMap);
            this.registerPoolPoint(edge.getEndPoint(), pointRecordMap, serializableObjectIdMap);
            return {
                entityId: edge.persistentId,
                kind: 'parabola',
                focalWorld: toVector3Tuple(definition.focalWorld),
                apexWorld: toVector3Tuple(definition.apexWorld),
                startParameter: definition.startParameter,
                endParameter: definition.endParameter,
                segments: definition.segments,
                focalPointId: edge.getFocalPoint().persistentId,
                apexPointId: edge.getApexPoint().persistentId,
                startPointId: edge.getStartPoint().persistentId,
                endPointId: edge.getEndPoint().persistentId,
                isConstruction: edge.isConstruction,
            };
        }

        if (isSketchSplineObject(edge) || isSketchStyleSplineObject(edge)) {
            const controlPoints = edge.getControlPoints();
            controlPoints.forEach((point) => {
                this.registerPoolPoint(point, pointRecordMap, serializableObjectIdMap);
            });
            const metadata = edge.getCurveMetadata();
            return {
                entityId: edge.persistentId,
                kind: 'spline',
                variant: isSketchStyleSplineObject(edge) ? 'style' : 'standard',
                controlPointIds: controlPoints.map((point) => point.persistentId),
                curvePoints: edge.getCurvePoints().map((point) => toVector3Tuple(point)),
                curveMetadata: {
                    selectionKind: metadata.selectionKind,
                    curveKind: metadata.curveKind,
                    closureKind: metadata.closureKind,
                    styleSplineMode: metadata.styleSplineMode,
                    degree: metadata.degree,
                    weights: [...metadata.weights],
                    isRational: metadata.isRational,
                },
                curveEvaluationDefinition: edge.getCurveEvaluationDefinition() ?? undefined,
                isConstruction: edge.isConstruction,
            };
        }

        throw new Error(
            `草图持久化暂不支持实体类型 ${String(Reflect.get(edge, 'objectType'))}（persistentId=${edge.persistentId}），禁止静默丢弃。`,
        );
    }

    /**
     * 序列化槽口容器。
     * @param slot 槽口对象。
     * @param pointRecordMap 点池记录映射。
     * @param serializableObjectIdMap 可序列化对象到持久 ID 的映射。
     * @returns 槽口记录。
     */
    private serializeSlot(
        slot: SketchSlotObject,
        pointRecordMap: Map<string, SketchPersistentPointRecord>,
        serializableObjectIdMap: Map<ISketchObject, string>,
    ): SketchPersistentEntityRecord {
        serializableObjectIdMap.set(slot as unknown as ISketchObject, slot.persistentId);
        const junction = slot.junctionPoints;
        [junction.p1, junction.p2, junction.p3, junction.p4].forEach((point) => {
            this.registerPoolPoint(point, pointRecordMap, serializableObjectIdMap);
        });

        const geometryData = (slot.geometryData
            ? JSON.parse(JSON.stringify(slot.geometryData))
            : null) as SketchPersistentPlainData;

        if (slot.isArcSlot()) {
            const arcCenterPoint = slot.arcCenterPoint;
            const centerArcMidpoint = slot.centerArcMidpoint;
            if (
                !slot.outerArc
                || !slot.innerArc
                || !arcCenterPoint
                || !centerArcMidpoint
                || !slot.constructionCenterlineArc
            ) {
                throw new Error(`草图持久化失败：圆弧槽口字段缺失（persistentId=${slot.persistentId}）。`);
            }
            this.registerPoolPoint(arcCenterPoint, pointRecordMap, serializableObjectIdMap);
            this.registerPoolPoint(centerArcMidpoint, pointRecordMap, serializableObjectIdMap);
            return {
                entityId: slot.persistentId,
                kind: 'slot',
                mode: slot.mode,
                slotKind: 'arc',
                startCapArcId: slot.startCapArc.persistentId,
                endCapArcId: slot.endCapArc.persistentId,
                outerArcId: slot.outerArc.persistentId,
                innerArcId: slot.innerArc.persistentId,
                constructionCenterlineArcId: slot.constructionCenterlineArc.persistentId,
                arcCenterPointId: arcCenterPoint.persistentId,
                centerArcMidpointId: centerArcMidpoint.persistentId,
                junctionPointIds: {
                    p1: junction.p1.persistentId,
                    p2: junction.p2.persistentId,
                    p3: junction.p3.persistentId,
                    p4: junction.p4.persistentId,
                },
                geometryData,
                isConstruction: slot.isConstruction,
                hasContour: slot.contour !== null,
            };
        }

        const centerlineStartPoint = slot.centerlineStartPoint;
        const centerlineEndPoint = slot.centerlineEndPoint;
        const centerPoint = slot.centerPoint;
        if (!slot.topLine || !slot.bottomLine || !slot.constructionCenterline
            || !centerlineStartPoint || !centerlineEndPoint || !centerPoint) {
            throw new Error(`草图持久化失败：直槽口字段缺失（persistentId=${slot.persistentId}）。`);
        }
        this.registerPoolPoint(centerlineStartPoint, pointRecordMap, serializableObjectIdMap);
        this.registerPoolPoint(centerlineEndPoint, pointRecordMap, serializableObjectIdMap);
        this.registerPoolPoint(centerPoint, pointRecordMap, serializableObjectIdMap);
        return {
            entityId: slot.persistentId,
            kind: 'slot',
            mode: slot.mode,
            slotKind: 'straight',
            startCapArcId: slot.startCapArc.persistentId,
            endCapArcId: slot.endCapArc.persistentId,
            topLineId: slot.topLine.persistentId,
            bottomLineId: slot.bottomLine.persistentId,
            constructionCenterlineId: slot.constructionCenterline.persistentId,
            centerlineStartPointId: centerlineStartPoint.persistentId,
            centerlineEndPointId: centerlineEndPoint.persistentId,
            centerPointId: centerPoint.persistentId,
            junctionPointIds: {
                p1: junction.p1.persistentId,
                p2: junction.p2.persistentId,
                p3: junction.p3.persistentId,
                p4: junction.p4.persistentId,
            },
            geometryData,
            isConstruction: slot.isConstruction,
            hasContour: slot.contour !== null,
        };
    }

    /**
     * 序列化集合容器。
     * @param collection 集合对象。
     * @param pointRecordMap 点池记录映射。
     * @param serializableObjectIdMap 可序列化对象到持久 ID 的映射。
     * @returns 集合记录。
     */
    private serializeCollection(
        collection: SketchCollectionObject,
        pointRecordMap: Map<string, SketchPersistentPointRecord>,
        serializableObjectIdMap: Map<ISketchObject, string>,
    ): SketchPersistentEntityRecord {
        serializableObjectIdMap.set(collection as unknown as ISketchObject, collection.persistentId);
        const sourceEntityIds = [...collection.collectSet].map((source) => source.persistentId);
        const sourceSet = new Set<THREE.Object3D>([...collection.collectSet] as unknown as THREE.Object3D[]);

        // 直接子对象补齐：多边形辅助圆、矩形构造对角线与中心草图点等不在 collectSet 中的定义组成部分。
        const directChildEntityIds: string[] = [];
        const directChildPointIds: string[] = [];
        collection.children.forEach((child) => {
            if (sourceSet.has(child)) {
                return;
            }
            if (isSketchPointObject(child)) {
                this.registerPoolPoint(child, pointRecordMap, serializableObjectIdMap);
                directChildPointIds.push(child.persistentId);
                return;
            }
            if (
                isSketchLineObject(child) || isSketchArcObject(child) || isSketchCircleObject(child)
                || isSketchEllipseObject(child) || isSketchParabolaObject(child)
                || isSketchSplineObject(child) || isSketchStyleSplineObject(child)
            ) {
                directChildEntityIds.push((child as AbstractSketchEdgeObject).persistentId);
            }
        });

        return {
            entityId: collection.persistentId,
            kind: 'collection',
            creationSource: collection.creationSource,
            sourceEntityIds,
            directChildEntityIds: directChildEntityIds.sort((left, right) => left.localeCompare(right)),
            directChildPointIds: directChildPointIds.sort((left, right) => left.localeCompare(right)),
            geometryData: (collection.geometryData
                ? JSON.parse(JSON.stringify(collection.geometryData))
                : null) as SketchPersistentPlainData,
            hasContour: Reflect.get(collection, 'contour') !== null,
        };
    }

    /**
     * 把点登记进点池（共享点按持久 ID 去重）。
     * @param point 点对象。
     * @param pointRecordMap 点池记录映射。
     * @param serializableObjectIdMap 可序列化对象到持久 ID 的映射。
     */
    private registerPoolPoint(
        point: SketchPointObject,
        pointRecordMap: Map<string, SketchPersistentPointRecord>,
        serializableObjectIdMap: Map<ISketchObject, string>,
    ): void {
        serializableObjectIdMap.set(point, point.persistentId);
        if (pointRecordMap.has(point.persistentId)) {
            return;
        }
        pointRecordMap.set(point.persistentId, {
            pointId: point.persistentId,
            position: toVector3Tuple(point.getPosition()),
            pointKind: point.pointKind,
            ...(point.boxSelectionRole !== SketchPointBoxSelectionRole.Default
                ? {boxSelectionRole: point.boxSelectionRole}
                : {}),
            ...(point.isAuxiliaryObject === true ? {isAuxiliaryObject: true} : {}),
        });
    }

    /**
     * 收集草图组直接子点（独立草图点、矩形构造线交点、倒角原交点焦点等）。
     * 说明：
     * 1. 判据是组直接子对象，与运行时挂载结构一致；点池去重与对象身份登记统一由 registerPoolPoint 收口；
     * 2. 曲线内部自建控制点（象限点、圆心等）是实体子对象，不会误入；
     * 3. 会话级显示辅助点不进入持久化文档。
     * @param group 目标草图组。
     * @param pointRecordMap 点池记录映射。
     * @param serializableObjectIdMap 可序列化对象到持久 ID 的映射。
     */
    private collectFreePoints(
        group: SketchGroup,
        pointRecordMap: Map<string, SketchPersistentPointRecord>,
        serializableObjectIdMap: Map<ISketchObject, string>,
    ): void {
        group.getSketchPointObjects().forEach((point) => {
            if (point.parent !== (group as unknown as THREE.Object3D)) {
                return;
            }
            if (
                point.isDrawingPreviewPoint
                || point.isDrawingSketchOrigin
                || point.isSketchSessionReferencePoint
            ) {
                return;
            }
            this.registerPoolPoint(point, pointRecordMap, serializableObjectIdMap);
        });
    }

    /**
     * 序列化几何关系。
     * @param serializableObjectIdMap 可序列化对象到持久 ID 的映射。
     * @returns 关系记录列表。
     */
    private serializeRelations(
        serializableObjectIdMap: ReadonlyMap<ISketchObject, string>,
    ): SketchPersistentRelationRecord[] {
        const visitedConstraintSet = new Set<SketchRelationDefinition<ISketchObject>>();
        const relations: SketchPersistentRelationRecord[] = [];

        serializableObjectIdMap.forEach((_objectId, sketchObject) => {
            const constraints = sketchObject.constraints?.getRelations?.() ?? [];
            constraints.forEach((constraint) => {
                const normalizedConstraint = constraint as SketchRelationDefinition<ISketchObject>;
                if (visitedConstraintSet.has(normalizedConstraint)) {
                    return;
                }
                visitedConstraintSet.add(normalizedConstraint);

                /**
                 * 转换实体引用的 OnEdge 是 Record 在求解层的派生关系。
                 * 只有携带稳定 referenceId 的关系才属于该派生视图；普通 OnEdge 仍必须显式失败，
                 * 避免把尚未建模的关系类型静默丢弃。
                 */
                if (normalizedConstraint.relationType === SketchRelationKind.OnEdge) {
                    const referenceId = Reflect.get(normalizedConstraint, 'referenceId');
                    if (typeof referenceId === 'string' && referenceId.length > 0) {
                        return;
                    }
                }

                if (!resolveSupportedPersistentRelationKindSet().has(normalizedConstraint.relationType)) {
                    throw new Error(
                        `草图持久化暂不支持关系类型 ${normalizedConstraint.relationType}（persistentId=${normalizedConstraint.persistentId}），禁止静默丢弃。`,
                    );
                }

                const targetIds = normalizedConstraint.getTargetsSnapshot().map((target) => {
                    const targetId = serializableObjectIdMap.get(target);
                    if (!targetId) {
                        throw new Error(
                            `草图持久化遇到引用不可序列化目标的关系 ${normalizedConstraint.relationType}（persistentId=${normalizedConstraint.persistentId}），禁止静默丢弃。`,
                        );
                    }
                    return targetId;
                });

                relations.push({
                    constraintId: normalizedConstraint.persistentId,
                    relationKind: normalizedConstraint.relationType,
                    targetIds,
                    numericValue: this.resolveNumericValue(normalizedConstraint),
                    suppressed: normalizedConstraint.isSuppressed,
                    branchHint: normalizedConstraint.getBranchHint(),
                    ...(() => {
                        const formulaPayload = normalizedConstraint.createParametricFormulaPayload();
                        return formulaPayload === null ? {} : {formulaPayload};
                    })(),
                });
            });
        });

        return relations;
    }

    /**
     * 读取数值关系的锁定值。
     * @param constraint 约束实例。
     * @returns 锁定值；无数值语义或未锁定时为 null。
     */
    private resolveNumericValue(constraint: SketchRelationDefinition<ISketchObject>): number | null {
        // 数值语义单点声明在关系定义表，与反序列化侧共用同一份清单，杜绝"存得下读不回"。
        return readSketchRelationNumericValue(constraint.relationType, constraint);
    }
}
